import { useMemo } from "react";

import { useRightRail } from "@/context/RightRailContext";
import { useKaiChat } from "@/features/kai/KaiChatContext";
import {
  KAI_ENABLED,
  KAI_INSIGHTS,
  MICROSOFT_GRAPH_EMAIL,
  useFeatureEnabled,
} from "@/context/FeatureFlagsContext";
import {
  computeRailStackLayout,
  nextFocusPanel,
  railPanelFlags,
  RAIL_PANEL_ORDER,
  type RailStackLayout,
  type RailStackPanelId,
} from "@/shell/railStackLayout";

export interface UseRailStackLayoutOptions {
  /** Mobile drawer flow layout instead of the fixed-height desktop stack. */
  stack: boolean;
  /** Notes / to-do / recent activity card present. */
  showWorkspace: boolean;
  /** Page-rail slot (Opportunities / filters) present. */
  showPageRail: boolean;
  /** Kai landing page — the Kai card is not shown in this rail. */
  kaiPage: boolean;
}

export interface RailStackLayoutResult {
  layout: RailStackLayout;
  /** The focused / dominant card, or `null` when nothing is expanded. */
  focusedPanel: RailStackPanelId | null;
  /** Booking-detail rail (Opportunities + Documents + Insight cards). */
  bookingDetailMode: boolean;
  /** Page-rail card is present in this rail. */
  pageRailActive: boolean;
  /** An overlay (notifications / rate line) currently owns the rail. */
  monopolize: boolean;
}

/**
 * Resolve the desktop side-rail card layout from the shared rail state, feature
 * flags, and the current `RightRailContent` props. This is the single hook the
 * view uses to decide how cards share the rail's fixed height.
 */
export function useRailStackLayout({
  stack,
  showWorkspace,
  showPageRail,
  kaiPage,
}: UseRailStackLayoutOptions): RailStackLayoutResult {
  const {
    kaiPanelActive,
    kaiSectionOpen,
    notesSectionOpen,
    pageRailMeta,
    pageRailSectionOpen,
    opportunityRailMeta,
    opportunitySectionOpen,
    lookupRailMeta,
    lookupSectionOpen,
    blDocumentRailMeta,
    blDocumentSectionOpen,
    bookingInsightSourceId,
    bookingInsightSectionOpen,
    bookingDocumentsSectionOpen,
    bookingBlGroupSourceId,
    bookingBlGroupSectionOpen,
    bookingAssignedRateRail,
    bookingAssignedRateSectionOpen,
    bookingMatchedScheduleRail,
    bookingMatchedScheduleSectionOpen,
    expandedPanel,
    notificationsPanelActive,
    selectedRateLine,
  } = useRightRail();
  const kai = useKaiChat();
  const historyOpen = kai?.historyOpen ?? false;

  const kaiEnabled = useFeatureEnabled(KAI_ENABLED);
  const kaiInsightsEnabled = useFeatureEnabled(KAI_INSIGHTS);
  const graphEmailEnabled = useFeatureEnabled(MICROSOFT_GRAPH_EMAIL);

  const bookingDetailMode = bookingInsightSourceId != null;
  const rateLineActive = selectedRateLine != null;
  const monopolize = notificationsPanelActive || rateLineActive;

  return useMemo(() => {
    const present = railPanelFlags({
      page: showPageRail && pageRailMeta != null,
      opportunity: opportunityRailMeta != null,
      lookup: lookupRailMeta != null,
      blDocument: blDocumentRailMeta != null,
      blGroup: bookingBlGroupSourceId != null,
      assignedRate: bookingAssignedRateRail != null,
      matchedSchedule: bookingMatchedScheduleRail != null,
      documents: graphEmailEnabled && bookingDetailMode,
      insight: kaiEnabled && kaiInsightsEnabled && bookingDetailMode,
      kai: kaiPanelActive && !kaiPage,
      notes: showWorkspace,
    });
    const userOpen = railPanelFlags({
      page: pageRailSectionOpen,
      opportunity: opportunitySectionOpen,
      lookup: lookupSectionOpen,
      blDocument: blDocumentSectionOpen,
      blGroup: bookingBlGroupSectionOpen,
      assignedRate: bookingAssignedRateSectionOpen,
      matchedSchedule: bookingMatchedScheduleSectionOpen,
      documents: bookingDocumentsSectionOpen,
      insight: bookingInsightSectionOpen,
      kai: kaiSectionOpen,
      notes: notesSectionOpen,
    });

    const requestedFocus: RailStackPanelId | null = historyOpen
      ? "kai"
      : (RAIL_PANEL_ORDER as readonly string[]).includes(expandedPanel)
        ? (expandedPanel as RailStackPanelId)
        : null;

    const layout = computeRailStackLayout({
      present,
      userOpen,
      focused: requestedFocus,
      monopolize,
      stack,
    });

    const focusedPanel = nextFocusPanel(userOpen, present, requestedFocus);

    return {
      layout,
      focusedPanel,
      bookingDetailMode,
      pageRailActive: present.page,
      monopolize,
    };
  }, [
    showPageRail,
    pageRailMeta,
    opportunityRailMeta,
    opportunitySectionOpen,
    lookupRailMeta,
    lookupSectionOpen,
    blDocumentRailMeta,
    blDocumentSectionOpen,
    graphEmailEnabled,
    bookingDetailMode,
    kaiEnabled,
    kaiInsightsEnabled,
    kaiPanelActive,
    kaiPage,
    showWorkspace,
    pageRailSectionOpen,
    bookingBlGroupSourceId,
    bookingBlGroupSectionOpen,
    bookingAssignedRateRail,
    bookingAssignedRateSectionOpen,
    bookingMatchedScheduleRail,
    bookingMatchedScheduleSectionOpen,
    bookingDocumentsSectionOpen,
    bookingInsightSectionOpen,
    kaiSectionOpen,
    notesSectionOpen,
    historyOpen,
    expandedPanel,
    monopolize,
    stack,
  ]);
}
